import cn from 'classnames'

import { Translation } from 'cng-common/features/translations'
import { Button } from 'core-components/library/button'

import { PLANNING_SMB_TRANSLATION_KEYS, SMB_PERCENT_FRACTION_DIGITS } from '../../configs'
import { TSmbSplitResult } from '../../model/types'

import styles from './PlanningSmbCell.module.scss'

const KEYS = PLANNING_SMB_TRANSLATION_KEYS.cell
const TREND_EPSILON = 0.5

interface IPlanningSmbStatusProps {
  summary: TSmbSplitResult
  baselineSummary?: TSmbSplitResult
}

const getTrend = (value: number, baseline: number) => {
  const difference = value - baseline

  if (Math.abs(difference) < TREND_EPSILON) {
    return null
  }

  return difference > 0 ? 'up' : 'down'
}

const TrendArrow = ({ trend }: { trend: 'up' | 'down' | null }) =>
  trend ? (
    <span
      className={cn(styles.trendArrow, trend === 'up' ? styles.trendUp : styles.trendDown)}
      aria-hidden
    >
      {trend === 'up' ? '▲' : '▼'}
    </span>
  ) : null

export const PlanningSmbStatus = ({ summary, baselineSummary }: IPlanningSmbStatusProps) => {
  const isPlanned = !summary.isEmpty
  const smbTrend =
    !summary.isEmpty && baselineSummary && !baselineSummary.isEmpty
      ? getTrend(summary.smb, baselineSummary.smb)
      : null
  const euTrend =
    !summary.isEmpty && baselineSummary && !baselineSummary.isEmpty
      ? getTrend(summary.eu, baselineSummary.eu)
      : null

  return (
    <span className={cn(styles.status, { [styles.statusPlanned]: isPlanned })}>
      <span className={styles.statusDot} />
      <Translation span text={isPlanned ? KEYS.planned : KEYS.notPlanned} />
      {!summary.isEmpty && (
        <span className={styles.splitPill}>
          <span className={styles.pillSmb}>
            SMB {summary.smb.toFixed(SMB_PERCENT_FRACTION_DIGITS)}%
            <TrendArrow trend={smbTrend} />
          </span>
          <span className={styles.pillEu}>
            EU {summary.eu.toFixed(SMB_PERCENT_FRACTION_DIGITS)}%
            <TrendArrow trend={euTrend} />
          </span>
        </span>
      )}
    </span>
  )
}

export interface IPlanningSmbCellProps {
  /** The category own split, or an empty result when nothing is planned yet */
  summary: TSmbSplitResult
  /** Previous-year PlanningSMB split used only for directional trend arrows. */
  baselineSummary?: TSmbSplitResult
  showAction: boolean
  onOpen: () => void
  /** Opens Guided Setup for the whole Offering from either Total Planning or a category. */
  showGuidedAction?: boolean
  onOpenGuided?: () => void
}

/**
 * Status of the SMB vs EU split plus the action that opens it. The status pill is purely
 * informational - the button is the action, solid while nothing is planned yet (a clear call
 * to action) and a lighter outline once planned (a lower emphasis follow up).
 */
export const PlanningSmbCell = ({
  summary,
  baselineSummary,
  showAction,
  onOpen,
  showGuidedAction = false,
  onOpenGuided,
}: IPlanningSmbCellProps) => {
  const isPlanned = !summary.isEmpty

  return (
    <div className={styles.cell}>
      <span className={styles.label}>
        <Translation span text={KEYS.label} />
      </span>
      <PlanningSmbStatus summary={summary} baselineSummary={baselineSummary} />
      {showGuidedAction && onOpenGuided && (
        <Button
          className={styles.guidedButton}
          size="sm"
          color="primary"
          variant="contained"
          onClick={onOpenGuided}
        >
          <Translation span text={KEYS.guidedSetup} />
        </Button>
      )}
      {showAction && (
        <Button
          className={styles.actionButton}
          size="sm"
          color="primary"
          variant={showGuidedAction || isPlanned ? 'outlined' : 'contained'}
          onClick={onOpen}
        >
          <Translation span text={isPlanned ? KEYS.updatePlan : KEYS.plan} />
        </Button>
      )}
    </div>
  )
}
